import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { FiUploadCloud, FiSearch, FiTrash2, FiMove } from 'react-icons/fi';
import Notification, { showToast } from '../../../components/common/notification/Notification';
import './ImageBannerManagement.css';

const ImageBannerManagement = () => {
  const loggedInUser = useSelector((state) => state.auth.user);
  const token = loggedInUser?.refreshToken || loggedInUser?.token;
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const assetUrl = process.env.REACT_APP_ASSET_URL;

  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPage, setFilterPage] = useState('ALL');

  // Upload Form State
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    page: 'SLIDER',
    display_order: 1
  });
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Drag state
  const [isDragActive, setIsDragActive] = useState(false);

  const fetchImages = useCallback(async () => {
    setLoading(true);
    try {
      // The API is paginated, but for management we can fetch a large page size for now
      const payload = {
        page_number: 1,
        page_size: 100,
        order_by: "ASC",
        keyword_search: searchTerm
      };
      const response = await axios.post(`${baseUrl}/admin/images/list`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let fetchedImages = [];
      if (response.data?.imageAssets && Array.isArray(response.data.imageAssets)) {
          fetchedImages = response.data.imageAssets;
      } else if (Array.isArray(response.data)) {
          fetchedImages = response.data;
      }
      
      // Filter by page if needed locally (or if API supports it, but API only has keyword_search)
      if (filterPage !== 'ALL') {
        fetchedImages = fetchedImages.filter(img => img.page === filterPage);
      }
      
      // Sort by display order
      fetchedImages.sort((a, b) => a.display_order - b.display_order);
      setImages(fetchedImages);
    } catch (error) {
      console.error("Error fetching images:", error);
      showToast("Failed to fetch images", { type: "error" });
    } finally {
      setLoading(false);
    }
  }, [baseUrl, token, searchTerm, filterPage]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      fetchImages();
    }
  };

  // --- Upload Logic ---
  const handleDrag = function(e) {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragActive(true);
    } else if (e.type === "dragleave") {
      setIsDragActive(false);
    }
  };

  const handleDrop = function(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = function(e) {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file) => {
    if (file && file.size > 5 * 1024 * 1024) {
      showToast(`File size (${(file.size / (1024 * 1024)).toFixed(2)} MB) exceeds the 5 MB limit.`, { type: "error" });
      return;
    }
    setUploadFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      setUploadPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const submitUpload = async () => {
    if (!uploadFile) {
      showToast("Please select an image to upload", { type: "error" });
      return;
    }
    if (!formData.title) {
      showToast("Please provide a title", { type: "error" });
      return;
    }

    setIsUploading(true);
    try {
      // 1. Upload file to get URL
      const fileFormData = new FormData();
      fileFormData.append("files", uploadFile);
      
      const uploadRes = await axios.post(`${baseUrl}/all/upload/files/images`, fileFormData, {
        headers: { 
          "Content-Type": "multipart/form-data",
          // Authorization might not be strictly needed for /all/ endpoints, but good to pass if standard
        }
      });
      
      let imageUrl = "";
      if (uploadRes.data?.statusType === "SUCCESS" && uploadRes.data.propertyImages?.length > 0) {
        imageUrl = uploadRes.data.propertyImages[0].image_url;
      } else {
        throw new Error("Failed to get image URL from upload");
      }

      // 2. Create Image Record
      const payload = {
        image_url: imageUrl,
        title: formData.title,
        page: formData.page,
        display_order: parseInt(formData.display_order, 10) || 1
      };

      await axios.post(`${baseUrl}/admin/images`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      showToast("Image uploaded successfully!", { type: "success" });
      
      // Reset form
      setUploadFile(null);
      setUploadPreview(null);
      setFormData({ ...formData, title: '', display_order: parseInt(formData.display_order, 10) + 1 });
      
      // Refresh list
      fetchImages();
    } catch (error) {
      console.error("Upload error:", error);
      showToast("Failed to upload image", { type: "error" });
    } finally {
      setIsUploading(false);
    }
  };

  // --- Image Card Actions ---
  const toggleActive = async (image) => {
    try {
      await axios.put(`${baseUrl}/admin/images/state`, {
        id: image.id,
        is_active: !image.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast(`Image ${!image.is_active ? 'activated' : 'deactivated'}`, { type: "success" });
      fetchImages();
    } catch (error) {
      console.error("Toggle error:", error);
      showToast("Failed to update status", { type: "error" });
    }
  };

  const deleteImage = async (id) => {
    if (!window.confirm("Are you sure you want to delete this image?")) return;
    try {
      await axios.delete(`${baseUrl}/admin/images/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast("Image deleted", { type: "success" });
      fetchImages();
    } catch (error) {
      console.error("Delete error:", error);
      showToast("Failed to delete image", { type: "error" });
    }
  };

  const handleOrderChange = (id, newOrder) => {
    setImages(images.map(img => img.id === id ? { ...img, display_order: parseInt(newOrder, 10) || 0 } : img));
  };

  const saveOrder = async () => {
    try {
      const orders = images.map(img => ({ id: img.id, display_order: img.display_order }));
      await axios.patch(`${baseUrl}/admin/images/order/bulk`, { orders }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast("Order saved successfully", { type: "success" });
      fetchImages(); // Refresh to sort correctly
    } catch (error) {
      console.error("Save order error:", error);
      showToast("Failed to save order", { type: "error" });
    }
  };

  return (
    <div className="image-banner-container">
      <Notification />
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold text-dark m-0">
          <FiUploadCloud className="me-2 text-primary" /> 
          Image & Banner Management
        </h2>
      </div>

      {/* Upload Section */}
      <div className="upload-card p-4 mb-5">
        <h5 className="mb-4 text-primary fw-bold">Upload New Asset</h5>
        <div className="row g-4">
          <div className="col-md-5">
            <div 
              className={`dropzone-area ${isDragActive ? 'drag-active' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current.click()}
            >
              <input 
                ref={fileInputRef}
                type="file"
                className="d-none"
                accept="image/jpeg, image/png, image/webp"
                onChange={handleChange}
              />
              {uploadPreview ? (
                <img src={uploadPreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }} />
              ) : (
                <>
                  <FiUploadCloud className="dropzone-icon" />
                  <h6 className="fw-bold m-0">Drag & drop image here</h6>
                  <p className="text-muted small m-0 mt-1">or click to browse (JPG, PNG, WEBP)</p>
                </>
              )}
            </div>
          </div>
          <div className="col-md-7">
            <div className="mb-3">
              <label className="form-label fw-semibold">Title *</label>
              <input 
                type="text" 
                className="form-control" 
                placeholder="e.g. Summer Sale Banner" 
                value={formData.title}
                onChange={e => setFormData({...formData, title: e.target.value})}
              />
            </div>
            <div className="row mb-4">
              <div className="col-md-6">
                <label className="form-label fw-semibold">Target Page</label>
                <select 
                  className="form-select"
                  value={formData.page}
                  onChange={e => setFormData({...formData, page: e.target.value})}
                >
                  <option value="SLIDER">SLIDER</option>
                  <option value="BANNER">BANNER</option>
                  <option value="SPLASH_SCREEN">SPLASH SCREEN</option>
                  <option value="MOBILE_HOME">MOBILE HOME</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">Display Order</label>
                <input 
                  type="number" 
                  className="form-control" 
                  min="1"
                  value={formData.display_order}
                  onChange={e => setFormData({...formData, display_order: e.target.value})}
                />
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <button 
                className="btn btn-primary px-4 py-2" 
                style={{ backgroundColor: '#a5b4fc', borderColor: '#a5b4fc', color: '#312e81', fontWeight: 600 }}
                onClick={submitUpload}
                disabled={isUploading}
              >
                {isUploading ? "Uploading..." : <><FiUploadCloud className="me-2" /> Upload Image</>}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* List Section */}
      <div className="list-card p-4">
        <div className="action-bar">
          <div className="search-input-wrapper">
            <FiSearch className="search-icon" />
            <input 
              type="text" 
              className="search-input" 
              placeholder="Search by keyword..." 
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyDown={handleSearch}
            />
          </div>
          <div className="d-flex gap-3 ms-auto">
            <select 
              className="filter-select"
              value={filterPage}
              onChange={e => setFilterPage(e.target.value)}
            >
              <option value="ALL">All Pages</option>
              <option value="SLIDER">Slider</option>
              <option value="BANNER">Banner</option>
              <option value="SPLASH_SCREEN">Splash Screen</option>
              <option value="MOBILE_HOME">Mobile Home</option>
            </select>
            <button className="btn btn-outline-secondary d-flex align-items-center gap-2" onClick={saveOrder}>
              <FiUploadCloud style={{ transform: 'rotate(180deg)' }} /> Save Order
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="image-card-grid">
            {images.map((img) => (
              <div key={img.id} className="image-item-card">
                <div className="drag-handle">
                  <FiMove />
                </div>
                <div className={`page-badge ${img.page.toLowerCase()}`}>
                  {img.page.replace('_', ' ')}
                </div>
                <button className="delete-btn" onClick={() => deleteImage(img.id)} title="Delete Image">
                  <FiTrash2 />
                </button>
                <div className="image-preview-container">
                  <img src={img.image_url?.startsWith('http') ? img.image_url : `${assetUrl}${img.image_url}`} alt={img.title} className="image-preview" />
                </div>
                <div className="image-card-title">
                  <span className="text-truncate" style={{ maxWidth: '200px' }} title={img.title}>{img.title}</span>
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-muted small">#</span>
                    <input 
                      type="number" 
                      className="form-control form-control-sm text-center" 
                      style={{ width: '45px', padding: '2px 4px' }}
                      value={img.display_order}
                      onChange={(e) => handleOrderChange(img.id, e.target.value)}
                    />
                  </div>
                </div>
                <div className="image-card-footer">
                  <div className="status-indicator">
                    <div className={`status-dot ${img.is_active ? 'active' : ''}`}></div>
                    <span style={{ color: img.is_active ? '#10b981' : '#94a3b8' }}>
                      {img.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className={`custom-switch ${img.is_active ? 'active' : ''}`} onClick={() => toggleActive(img)}>
                    <div className="custom-switch-knob"></div>
                  </div>
                </div>
              </div>
            ))}
            {images.length === 0 && (
              <div className="col-12 text-center text-muted py-5">
                No images found.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageBannerManagement;
